import React from 'react';
import PropTypes from 'prop-types';
import { wrapFunctionalComponent } from 'utils/framework';
import {
    colors, radii, shadows, space
} from 'style/config';
import { Box, Icon } from 'components/ui';
import Empty from 'constants/empty';

const RADIUS = 2;

function Chiclet({
    radioProps,
    showX,
    clickOnX = false,
    isLarge,
    isActive,
    maxWidth,
    variant = 'outline',
    customPaddingX,
    children,
    customCSS = Empty.Object,
    onClick,
    display = 'inline-flex',
    alignItems = 'center',
    position = 'relative',
    ...props
}) {
    const ChicletWrapper = clickOnX ? Box : React.Fragment;
    const wrapperProps = clickOnX ? { display: 'inline-flex' } : {};

    const fontSize = isLarge ? 'base' : 'sm';
    const minHeight = isLarge ? 36 : 32;
    const defaultPadding = isLarge ? 4 : 3;
    const isChecked = isActive || radioProps?.checked;
    const activeState = isChecked ? 'active' : 'inactive';
    const baseCss = [styles[variant], styles[variant][activeState], customCSS];

    return (
        <ChicletWrapper {...wrapperProps}>
            <Box
                fontSize={fontSize}
                minHeight={minHeight}
                paddingX={customPaddingX || defaultPadding}
                lineHeight='tight'
                borderRadius={RADIUS}
                display={display}
                alignItems={alignItems}
                position={position}
                baseCss={baseCss}
                {...props}
                {...(radioProps && { is: 'label' })}
                {...(clickOnX ? { borderRadius: '4px 0px 0px 4px' } : { onClick })}
            >
                {radioProps && (
                    <input
                        type='radio'
                        {...radioProps}
                        css={styles.radioInput}
                    />
                )}
                <span
                    css={[styles[variant].label, maxWidth && [{ maxWidth }, styles.truncate]]}
                    children={children}
                />
                {showX && !clickOnX && (
                    <Icon
                        className='x'
                        name='x'
                        marginLeft={2}
                        size='10px'
                        css={styles[variant].x}
                    />
                )}
            </Box>
            {clickOnX && (
                <Box
                    display='flex'
                    alignItems='center'
                    borderLeft={1}
                    borderColor='white'
                    paddingRight={space[defaultPadding]}
                    paddingLeft={space[defaultPadding] - 1}
                    minHeight={minHeight}
                    borderRadius='0px 4px 4px 0'
                    baseCss={baseCss}
                    onClick={onClick}
                >
                    <Icon
                        className='x'
                        name='x'
                        size='10px'
                        css={styles[variant].x}
                    />
                </Box>
            )}
        </ChicletWrapper>
    );
}

const styles = {
    fill: {
        color: colors.black,
        backgroundColor: colors.lightGray,
        transition: 'background-color .2s',
        '.no-touch &:hover': {
            backgroundColor: colors.nearWhite
        }
    },
    outline: {
        color: colors.midGray,
        backgroundColor: colors.white,
        borderWidth: 1,
        borderColor: 'currentColor',
        transition: 'color .2s',
        '.no-touch &:hover .x': {
            color: colors.black
        },
        label: {
            color: colors.black
        },
        x: {
            color: colors.gray
        },
        '::before': {
            content: '""',
            position: 'absolute',
            inset: -1,
            border: '2px solid currentColor',
            borderRadius: radii[RADIUS],
            opacity: 0,
            transition: 'color .2s, opacity .2s'
        },
        active: {
            color: colors.black,
            '::before': {
                opacity: 1
            }
        },
        inactive: {
            '.no-touch &:hover::before, .no-touch button:hover > &::before': {
                opacity: 1
            }
        }
    },
    shadow: {
        color: colors.black,
        backgroundColor: colors.white,
        boxShadow: shadows.light,
        transition: 'color .2s',
        active: {
            '::before': {
                content: '""',
                position: 'absolute',
                inset: -2,
                border: '2px solid currentColor',
                borderRadius: radii[RADIUS]
            }
        },
        inactive: {
            '.no-touch &:hover': {
                color: colors.gray
            }
        }
    },
    truncate: {
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis'
    },
    radioInput: {
        position: 'absolute',
        opacity: 0
    }
};

Chiclet.propTypes = {
    isLarge: PropTypes.bool,
    showX: PropTypes.bool,
    clickOnX: PropTypes.bool,
    isActive: PropTypes.bool,
    maxWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    variant: PropTypes.oneOf(['outline', 'shadow', 'fill']),
    radioProps: PropTypes.object,
    customCSS: PropTypes.object,
    children: PropTypes.any.isRequired
};

export default wrapFunctionalComponent(Chiclet, 'Chiclet');
